import React from "react";
import { format, isValid, parse } from "date-fns";
import { useTipsDisplay } from "@/hooks/useTipsDisplay";
import { getRankingChange } from "@/utils/compare-rankings";
import logger from "@/utils/logger";
import { ChevronDown, ChevronUp, Circle } from "lucide-react";
import AnglesUpIcon from "./icons/angles-up-icon";
import BellIcon from "./icons/bell-icon";
import MedalIcon from "./icons/medal-icon";
import TrophyIcon from "./icons/trophy-icon";
import AppleTemplate from "/images/apple_template.png";

const AppleTemplateComponent = ({
  tip,
  firstFourData,
  rankingChanges = {},
  historicalData,
  selectedDate,
  screenshotUrl,
}) => {
  // Use the same custom hook as render-tips
  const { tipsCount } = useTipsDisplay();

  // Format the time with AM/PM
  const formatTimeWithAMPM = (value) => {
    if (!value) return "";
    try {
      if (value instanceof Date) {
        return isValid(value) ? format(value, "h:mma") : "";
      }
      if (typeof value === "number") {
        const d = new Date(value);
        return isValid(d) ? format(d, "h:mma") : "";
      }
      const raw = String(value).trim();
      if (!raw) return "";
      const asDate = new Date(raw);
      if (isValid(asDate) && /[T\s]/.test(raw)) {
        return format(asDate, "h:mma");
      }
      if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(raw)) {
        const today = new Date();
        const parsedTime =
          raw.split(":").length === 3
            ? parse(raw, "H:mm:ss", today)
            : parse(raw, "H:mm", today);
        return isValid(parsedTime) ? format(parsedTime, "h:mma") : raw;
      }
      return raw;
    } catch {
      return String(value);
    }
  };

  // Get formatted time values
  const tipsUpdatedTime = formatTimeWithAMPM(tip?.tipUpdatedAt) || "12:00PM";
  const resultedTime = formatTimeWithAMPM(tip?.resultedAt) || "10:30AM";
  const trackName = tip?.trackName || "Rockhamptom";
  const raceNumber = tip?.raceNumber || "5";

  // Get the second tip data if available (firstFourData would contain an array of tips)
  const secondTip =
    firstFourData && firstFourData[1]
      ? firstFourData[1]
      : {
          horseNumber: "2",
          horseName: "Second Pick",
          silks: null,
        };

  // Calculate ranking changes dynamically using the same pattern as render-tips.jsx
  const raceKey = `${trackName?.replace(/\s+/g, "-")}-${raceNumber}-${selectedDate}`;

  // Access both regular and early prediction data
  const regularData = historicalData?.[raceKey]?.predictionData;
  const earlyData = historicalData?.[raceKey]?.earlyPredictionData;

  // Get early predictions data
  const earlyPredictionsForRace = earlyData?.firstFourData || null;

  // Format the first horse entry as expected by getRankingChange
  const firstHorseEntry = {
    number: tip?.horseNumber,
    name: tip?.horseName,
    selectionId: tip?.selectionId,
    rank: 1,
  };

  // Format the second horse entry
  const secondHorseEntry = {
    number: secondTip?.horseNumber,
    name: secondTip?.horseName,
    selectionId: secondTip?.selectionId,
    rank: 2,
  };

  // Format the current predictions into the format expected by getRankingChange
  const currentPredictions =
    firstFourData?.map((horse, index) => ({
      number: horse.horseNumber,
      name: horse.horseName,
      selectionId: horse.selectionId,
      rank: index + 1,
    })) || [];

  // Get ranking changes using the same function and pattern as render-tips.jsx
  let firstRankingChange = rankingChanges[tip?.horseNumber] || "up"; // default fallback
  let secondRankingChange = rankingChanges[secondTip?.horseNumber] || "same"; // default fallback

  // Only calculate dynamic ranking if we have early prediction data
  if (
    earlyPredictionsForRace &&
    Array.isArray(earlyPredictionsForRace) &&
    earlyPredictionsForRace.length > 0
  ) {
    // Calculate first horse ranking change
    const dynamicFirstRankingChange = getRankingChange(
      firstHorseEntry,
      earlyPredictionsForRace,
      currentPredictions,
      tipsCount,
    );

    // Calculate second horse ranking change
    const dynamicSecondRankingChange = getRankingChange(
      secondHorseEntry,
      earlyPredictionsForRace,
      currentPredictions,
      tipsCount,
    );

    // Use calculated values if available
    if (dynamicFirstRankingChange) {
      firstRankingChange = dynamicFirstRankingChange;
    }

    if (dynamicSecondRankingChange) {
      secondRankingChange = dynamicSecondRankingChange;
    }
  }

  // Function to render ranking change icon
  const getRankingIcon = (rankingChange) => {
    switch (rankingChange) {
      case "up":
        return <ChevronUp className="w-4 h-4 text-green-500" />;
      case "down":
        return <ChevronDown className="w-4 h-4 text-red-500" />;
      case "double-up":
        return <AnglesUpIcon className="w-4 h-4 text-green-500" />;
      case "double-down":
        return <AnglesUpIcon className="w-4 h-4 text-red-500 rotate-180" />;
      case "same":
        return <Circle className="w-4 h-4 text-gray-400" />;
      default:
        return <div className="w-4 h-4"></div>; // Empty spacer
    }
  };

  // Function to get trimmed place getters - from render-tips.jsx
  const getTrimmedPlaceGetters = (data) => {
    if (!data) return [];

    const total = data.numberOfRunners || 4;

    let places;
    if (total <= 4) {
      places = 1;
    } else if (total <= 7) {
      places = 2;
    } else {
      places = 3;
    }

    return data.placeGetters?.slice(0, places) ?? [];
  };

  const getResultDisplay = (
    entry,
    isResult = false,
    earlyPredictionsData = null,
    isEarlyPrediction = false,
  ) => {
    // Add a safety check for entry
    if (!entry || !entry.horseName || !entry.horseNumber) {
      logger.warn("Invalid entry in getResultDisplay:", entry);
      return null;
    }

    // Use selectedDate if provided, otherwise default to today
    const today = selectedDate || format(new Date(), "dd-MM-yyyy");
    if (isResult === true) {
      const raceKey = `${trackName?.replace(/\s+/g, "-")}-${raceNumber}-${today}`;
      const raceResults = historicalData?.[raceKey];

      if (!raceResults?.betfairResults) {
        return null;
      }

      // Check for scratching first before anything else
      if (raceResults?.betfairResults?.scratchings) {
        const scratchings = raceResults.betfairResults.scratchings;
        const isScratched = scratchings?.some((scratching) => {
          const scratchingId = scratching?.selectionId?.toString();
          const entryId = entry?.selectionId?.toString();
          return scratchingId === entryId;
        });

        if (isScratched) {
          return (
            <span className="text-xs text-gray-500 mt-1 font-bold mr-1">
              SCR
            </span>
          );
        }
      }

      // Check winner
      if (
        raceResults?.betfairResults?.winner?.selectionId === entry?.selectionId
      ) {
        return (
          <TrophyIcon
            fill="#ff1493"
            className="mr-3 icon-fix"
            style={{ width: "18px", height: "18px", marginRight: "10px" }}
          />
        );
      }

      const trimmedPlaceGetters = getTrimmedPlaceGetters(
        raceResults?.betfairResults,
      );

      const isPlaced = trimmedPlaceGetters.some((placeGetter) => {
        const placeId = placeGetter?.selectionId?.toString();
        const entryId = entry?.selectionId?.toString();
        return placeId === entryId;
      });

      if (isPlaced) {
        return (
          <MedalIcon
            className="mr-3 icon-fix"
            style={{ width: "18px", height: "18px", marginRight: "10px" }}
          />
        );
      }

      // If we haven't returned by now, the horse was unplaced
      return (
        <span className="text-xs text-gray-500 mt-1 font-bold mr-1">UPL</span>
      );
    } else {
      // This code should never be reached with our updated logic, but keeping as fallback
      return null;
    }
  };

  return (
    <div className="absolute rotate-[3.92deg] -bottom-2 -left-1 flex justify-center overflow-hidden">
      <img
        src={AppleTemplate}
        alt="Apple Template"
        className="w-[236px] h-auto z-10"
        id="apple-template"
        crossOrigin="anonymous"
      />
      <>
        {screenshotUrl ? (
          <img
            src={screenshotUrl}
            alt="Screenshot"
            crossOrigin="anonymous"
            className="absolute -bottom-[358px] left-[10.5px] w-[213px] rounded-tl-[10.5px] z-50 overflow-hidden h-auto object-cover"
          />
        ) : (
          <>
            <div className="h-fit bg-white absolute top-7 left-8 z-50 text-[10px] font-semibold">
              {format(new Date(), "HH:mm")}
            </div>
            <div className="absolute -bottom-0.5 left-3 w-[230px] rounded-t-lg z-50 overflow-hidden h-[125px] bg-white">
              <div className="flex border-b-2 border-[#f30099] w-full gap-x-4 mt overflow-hidden">
                <div className="w-32 py-1 bg-[#6b7280] rounded-t-md text-white flex justify-start px-2">
                  <p className="text-xs text-nowrap r-1">{trackName}</p>
                </div>
                <div className="w-32 py-1 bg-[#e5e5e5] rounded-t-md text-[#6b7280] flex justify-start px-2">
                  <p className="text-xs r-1">Race {raceNumber}</p>
                </div>
              </div>

              {/* Time indicators */}
              <div className="flex w-full min-h-[1em] mt-0.5 ml-2">
                <div className="flex items-center justify-start text-xxs text-brand-pink">
                  <BellIcon className="w-3 h-3 transform translate-y-[-1px] animate-bell-ring" />
                  <span className="uppercase text-[7px]">
                    Tips <span className="font-bold">Updated</span> at{" "}
                    {tipsUpdatedTime}
                  </span>
                </div>
                <div className="flex ml-6 items-center justify-start text-xxs text-brand-pink">
                  <span className="uppercase text-[8px]">
                    RESULTED @ {resultedTime}
                  </span>
                </div>
              </div>

              {/* First Horse (winner) */}
              <div className="flex items-center w-full pl-[0.5rem] mt-2">
                <div className="flex items-center w-full">
                  <div className="tip-item-delta-container flex items-center justify-center w-4">
                    {getRankingIcon(firstRankingChange)}
                  </div>
                  <img
                    src={
                      tip?.silks ||
                      "https://www.puntingform.com.au/images/jockeysilks/1117607.jpeg"
                    }
                    alt="Jockey silk"
                    className="w-6 h-6 object-contain"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />

                  <div className="ml-1 tip-item-name-container text-xs flex flex-col gap-1">
                    <span className="font-semibold horse-name">
                      {tip?.horseNumber || "1"}.{" "}
                      {tip?.horseName || "Jockey Name"}
                    </span>
                  </div>
                </div>
                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                  <TrophyIcon
                    fill="#ff1493"
                    className="mr-3 icon-fix"
                    style={{ width: "18px", height: "18px", marginRight: "0" }}
                  />
                </div>
              </div>

              {/* Second Horse */}
              <div className="flex items-center w-full pl-[0.5rem] mt-2 s-horse">
                <div className="flex items-center w-full">
                  <div className="tip-item-delta-container flex items-center justify-center w-4">
                    {getRankingIcon(secondRankingChange)}
                  </div>
                  <img
                    src={
                      secondTip?.silks ||
                      "https://www.puntingform.com.au/images/jockeysilks/1117607.jpeg"
                    }
                    alt="Jockey silk"
                    className="w-6 h-6 object-contain opacity-90"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />

                  <div className="ml-1 tip-item-name-container text-xs flex flex-col gap-1">
                    <span className="font-semibold horse-name">
                      {secondTip?.horseNumber || "2"}.{" "}
                      {secondTip?.horseName || "Second Pick"}
                    </span>
                  </div>
                </div>
                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                  {getResultDisplay(secondTip, true, null)}
                </div>
              </div>
            </div>
          </>
        )}
      </>
    </div>
  );
};

export default AppleTemplateComponent;
